import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import {
  CREATE_RESPONSE_PLAN,
  DELETE_RESPONSE_PLAN,
  FIND_ALL_RESPONSE_PLANS_BY_PLAN,
  UPDATE_RESPONSE_PLAN,
} from '../../../queries';
import TitleBar from '../../../../../components/TitleBar';
import { Footer } from '../../../../../components/common/Footer';
import useBCPMetaData from '../../../../../hooks/useBCPMetaData';
import { Loading } from '../../../../../components';

import { ColumnsType } from 'antd/es/table';

import { phoneFormatter, tableSortingMethods } from '../../../../../utils';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../../../components/generic/Table/TableV3';
import QueryErrorBoundary from '../../../../../components/error-handling';
import { formFields } from './formFields';
import { baseColumns } from './exportColumns';
import TitleUpdater from '../../../../../components/common/TitleUpdater';

type EmergencyResponsePlan = {
  email: string;
  phone: string;
  planOwnerfirstName: string;
  planOwnerlastName: string;
  thread: string;
  uuid: string;
};

export default function EmergencyResponsePlan() {
  const params = useParams();
  const { type, locationId } = params;

  const isLocationType = type === 'location';

  const { planUUID, locationName, loading, error } = useBCPMetaData(
    locationId!,
    isLocationType,
  );

  const backPath = `/bcp-landing/${type}/${locationId}`;

  const columns: ColumnsType<any> = [
    {
      title: 'Plan Type/Threat',
      dataIndex: 'thread',
      key: 'thread',
      align: 'left',
      width: 150,
      sorter: (a, b) => tableSortingMethods.string(a.thread, b.thread),
      render: (_, { thread }) => (
        <div className="custom-table--column custom-table--column-primary">
          {thread}
        </div>
      ),
    },
    {
      title: 'Plan Owner',
      key: 'planOwner',
      align: 'left',
      width: 150,
      sorter: (a, b) =>
        tableSortingMethods.string(a.planOwnerfirstName, b.planOwnerfirstName),
      render: (_, { planOwnerfirstName, planOwnerlastName }) =>
        planOwnerfirstName || planOwnerlastName ? (
          <div className="custom-table--column">
            {planOwnerfirstName} {planOwnerlastName}
          </div>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      title: 'Contact',
      key: 'contact',
      align: 'left',
      width: 100,
      sorter: (a, b) => tableSortingMethods.string(a.email, b.email),
      render: (_, { email, phone }) =>
        email || phone ? (
          <div className="flex flex-col custom-table--column text-sm">
            <span>{email}</span>
            <span>{phoneFormatter(phone)}</span>
          </div>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
  ];

  const {
    data,
    loading: planLoading,
    error: planError,
  } = useQuery(FIND_ALL_RESPONSE_PLANS_BY_PLAN, {
    variables: { uuid: planUUID },
    skip: !planUUID,
  });

  const [createGQL] = useMutation(CREATE_RESPONSE_PLAN, {
    refetchQueries: ['FindAllResponsePlansByPlan'],
  });

  const [updateGQL] = useMutation(UPDATE_RESPONSE_PLAN, {
    refetchQueries: ['FindAllResponsePlansByPlan'],
  });

  const [deleteGQL] = useMutation(DELETE_RESPONSE_PLAN, {
    refetchQueries: ['FindAllResponsePlansByPlan'],
  });

  const addVariables = ({ values, type }: ModifyVariablesArgs) => {
    if (type === 'create') {
      return {
        ...values,
        plan: { uuid: planUUID },
      };
    }
    return {
      ...values,
    };
  };

  return (
    <div>
      <TitleUpdater title={`Emergency Response Plans`} />
      <TitleBar
        title="BCP Hub"
        subtitle="Emergency Response Plans"
        brief={
          isLocationType
            ? 'In the table below, capture the emergency response plans that exist at the location level.'
            : 'In the table below, capture the emergency response plans that exist at the corporate level.'
        }
      />
      <QueryErrorBoundary error={[error, planError]}>
        {!loading ? (
          <TableV3
            title="Emergency Response Plans"
            columns={columns}
            data={data?.findAll}
            loading={planLoading}
            editable
            formFields={formFields}
            createGQL={createGQL}
            updateGQL={updateGQL}
            deleteGQL={deleteGQL}
            modifyVariables={addVariables}
            exportExcelColumns={baseColumns}
          />
        ) : (
          <Loading />
        )}
      </QueryErrorBoundary>
      <Footer backPath={backPath} />
    </div>
  );
}
